import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Select from "react-select";
import countryList from "react-select-country-list";
import { State } from "country-state-city";
import { supabase } from "../../supabase";
import { specialties, roleCategories } from "../../constants/specialties";

const languages = [
    "English", "Afrikaans", "Zulu", "Xhosa", "French", "German", "Spanish", "Portuguese",
    "Arabic", "Mandarin", "Hindi", "Swahili", "Yoruba", "Igbo", "Hausa", "Italian",
    "Dutch", "Russian", "Japanese", "Korean", "Turkish", "Polish", "Greek", "Hebrew",
    "Urdu", "Bengali", "Tamil", "Telugu", "Marathi", "Gujarati", "Punjabi", "Malayalam",
    "Kannada", "Odia", "Assamese", "Nepali", "Sinhala", "Thai", "Vietnamese", "Indonesian",
    "Malay", "Tagalog", "Filipino", "Swedish", "Norwegian", "Danish", "Finnish", "Czech",
    "Hungarian", "Romanian", "Bulgarian", "Croatian", "Serbian", "Slovak", "Slovenian",
    "Lithuanian", "Latvian", "Estonian", "Ukrainian", "Persian", "Pashto", "Kurdish",
    "Amharic", "Somali", "Fulani", "Wolof", "Kinyarwanda", "Luganda", "Setswana", "Sesotho",
    "Tswana", "Venda", "Tsonga", "Ndebele", "Swati", "Northern Sotho", "Southern Sotho"
];

// Common currencies with codes
const currencies = [
    { code: "ZAR", symbol: "R", name: "South African Rand" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AUD", symbol: "A$", name: "Australian Dollar" },
    { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
    { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
    { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
    { code: "GHS", symbol: "₵", name: "Ghanaian Cedi" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
    { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
];

// Salary periods
const salaryPeriods = [
    { value: "yearly", label: "Yearly" },
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
    { value: "hourly", label: "Hourly" },
];

const INITIAL_FORM_STATE = {
    jobTitle: "",
    specialty: "",
    roleDescription: "",
    preferredCandidate: "",
    dutiesAndResponsibilities: "",
    employmentType: "",
    workPreference: "",
    country: "",
    countryCode: "",
    province: "",
    languageRequirement: [],
    locationOrLicenceRestrictions: null,
    locationOrLicenceRestrictionsText: "",
    requirementsSpecialty: "",
    experienceLevel: "",
    yearsOfExperience: "",
    budgetForRole: "",
    salaryCurrency: "ZAR",
    salaryPeriod: "yearly",
    requiredSkills: [],
    otherPreferences: "",
    screeningQuestions: false,
    driversLicenseRequired: false,
    driversLicenseAnswer: null,
    visaSponsorshipOffered: false,
    visaSponsorshipAnswer: null,
    about: "",
    is_dev_job: false
};

const JobPostFormModal = ({
    isOpen,
    mode = "edit",
    initialJob = null,
    employerUser = null,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const [formState, setFormState] = useState(INITIAL_FORM_STATE);
    const [jobFormStep, setJobFormStep] = useState(2);
    const [skillInput, setSkillInput] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [currentQuestionType, setCurrentQuestionType] = useState("multiple-choice"); // "multiple-choice" or "text"
    const [currentOptions, setCurrentOptions] = useState([""]);
    const [screeningQuestionsList, setScreeningQuestionsList] = useState([]);
    const [driversLicenseAnswer, setDriversLicenseAnswer] = useState(null);
    const [visaSponsorshipAnswer, setVisaSponsorshipAnswer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [isCustomSpecialty, setIsCustomSpecialty] = useState(false);

    const countries = useMemo(() => countryList().getData(), []);

    useEffect(() => {
        if (isOpen) {
            hydrateForm();
            setJobFormStep(2);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialJob, mode]);

    const hydrateForm = () => {
        if (mode === "edit" && initialJob) {
            const locationParts = (initialJob.location || "")
                .split(",")
                .map(part => part.trim())
                .filter(Boolean);

            const derivedCountry = initialJob.country || locationParts[locationParts.length - 1] || "";
            const derivedProvince = initialJob.province || (locationParts.length > 1 ? locationParts[0] : "");

            const requiredSkills = Array.isArray(initialJob.required_skills)
                ? initialJob.required_skills
                : Array.isArray(initialJob.requiredSkills)
                    ? initialJob.requiredSkills
                    : typeof initialJob.required_skills === "string"
                        ? initialJob.required_skills.split(",").map(skill => skill.trim()).filter(Boolean)
                        : [];

            const questionsList = Array.isArray(initialJob.screening_questions_list)
                ? initialJob.screening_questions_list
                : [];

            setFormState({
                jobTitle: initialJob.title || "",
                specialty: initialJob.specialty || "",
                roleDescription: initialJob.role_description || initialJob.job_description || "",
                preferredCandidate: initialJob.preferred_candidate || "",
                dutiesAndResponsibilities: initialJob.duties_and_responsibilities || "",
                employmentType: initialJob.employment_type || "",
                workPreference: initialJob.work_preference || "",
                country: derivedCountry,
                countryCode: initialJob.country_code || "",
                province: derivedProvince,
                languageRequirement: Array.isArray(initialJob.language_requirement) ? initialJob.language_requirement : [],
                locationOrLicenceRestrictions: initialJob.requires_license === true
                    ? "yes"
                    : initialJob.requires_license === false
                        ? "no"
                        : (initialJob.hiring_restrictions ? "yes" : null),
                locationOrLicenceRestrictionsText: initialJob.hiring_restrictions || "",
                requirementsSpecialty: initialJob.requirements_specialty || initialJob.specialty || "",
                experienceLevel: initialJob.experience_level || "",
                yearsOfExperience: initialJob.years_of_experience || "",
                budgetForRole: initialJob.budget_for_role || initialJob.salary_range || "",
                salaryCurrency: initialJob.salary_currency || "ZAR",
                salaryPeriod: initialJob.salary_period || "yearly",
                requiredSkills,
                otherPreferences: initialJob.other_preferences || "",
                screeningQuestions: Boolean(initialJob.screening_questions),
                driversLicenseRequired: Boolean(initialJob.drivers_license_required),
                driversLicenseAnswer: initialJob.drivers_license_answer || null,
                visaSponsorshipOffered: initialJob.visa_sponsorship_offered ?? initialJob.sponsorship_available ?? false,
                visaSponsorshipAnswer: initialJob.visa_sponsorship_answer || null,
                about: initialJob.job_description || "",
                is_dev_job: initialJob.is_dev_job || false
            });
            setSkillInput("");
            setCurrentQuestion("");
            setCurrentOptions([""]);
            setScreeningQuestionsList(questionsList);
            setDriversLicenseAnswer(initialJob.drivers_license_answer || null);
            setVisaSponsorshipAnswer(initialJob.visa_sponsorship_answer || null);
            
            // Check if specialty is custom (not in the list and not "Not applicable")
            const specialtyValue = initialJob.requirements_specialty || initialJob.specialty || "";
            const isCustom = specialtyValue && 
                           specialtyValue !== "Not applicable" && 
                           !specialties.includes(specialtyValue);
            setIsCustomSpecialty(isCustom);
        } else {
            resetForm();
        }
    };

    const resetForm = () => {
        setFormState(INITIAL_FORM_STATE);
        setSkillInput("");
        setCurrentQuestion("");
        setCurrentOptions([""]);
        setScreeningQuestionsList([]);
        setDriversLicenseAnswer(null);
        setVisaSponsorshipAnswer(null);
        setIsCustomSpecialty(false);
    };

    if (!isOpen) return null;

    const addSkill = (skill) => {
        if (skill.trim() && !formState.requiredSkills.includes(skill.trim())) {
            setFormState(prev => ({
                ...prev,
                requiredSkills: [...prev.requiredSkills, skill.trim()]
            }));
            setSkillInput("");
        }
    };

    const removeSkill = (index) => {
        setFormState(prev => ({
            ...prev,
            requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
        }));
    };

    // Specialty selection handlers
    const handleSpecialtySelect = (value) => {
        if (value === "Other") {
            setIsCustomSpecialty(true);
            setFormState(prev => ({ ...prev, requirementsSpecialty: "" }));
            return;
        }
        setIsCustomSpecialty(false);
        setFormState(prev => ({ ...prev, requirementsSpecialty: value }));
    };

    const resetSpecialtyToSelect = () => {
        setIsCustomSpecialty(false);
        setFormState(prev => ({ ...prev, requirementsSpecialty: "" }));
    };

    const addScreeningOption = () => {
        setCurrentOptions([...currentOptions, ""]);
    };

    const updateScreeningOption = (index, value) => {
        const updated = [...currentOptions];
        updated[index] = value;
        setCurrentOptions(updated);
    };

    const removeScreeningOption = (index) => {
        setCurrentOptions(currentOptions.filter((_, i) => i !== index));
    };

    const addScreeningQuestion = () => {
        if (currentQuestion.trim()) {
            if (currentQuestionType === "text") {
                const question = {
                    question: currentQuestion.trim(),
                    type: "text"
                };
                setScreeningQuestionsList([...screeningQuestionsList, question]);
                setCurrentQuestion("");
                setCurrentQuestionType("multiple-choice");
                setCurrentOptions([""]);
            } else {
                if (currentOptions.some(opt => opt.trim())) {
                    const question = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter(opt => opt.trim())
                    };
                    setScreeningQuestionsList([...screeningQuestionsList, question]);
                    setCurrentQuestion("");
                    setCurrentQuestionType("multiple-choice");
                    setCurrentOptions([""]);
                }
            }
        }
    };

    const removeScreeningQuestion = (index) => {
        setScreeningQuestionsList(screeningQuestionsList.filter((_, i) => i !== index));
    };

    const handleNextStep = () => {
        if (jobFormStep === 2) {
            if (!formState.jobTitle || !formState.specialty || !formState.employmentType || !formState.workPreference) {
                alert("Please fill in all required fields (Job Title, Role category, Employment Type, Work preference)");
                return;
            }
            setJobFormStep(3);
        }
    };

    const handleBackStep = () => {
        if (jobFormStep === 3) setJobFormStep(2);
    };

    const handleUpdateJob = async () => {
        if (!initialJob?.id || !employerUser?.id) return;
        setSaving(true);
        try {
            const locationParts = [];
            if (formState.province) locationParts.push(formState.province);
            if (formState.country) locationParts.push(formState.country);
            const location = locationParts.join(", ");

            // Include any unsaved screening question that's currently being typed
            let finalScreeningQuestionsList = [...screeningQuestionsList];
            if (currentQuestion.trim()) {
                if (currentQuestionType === "text") {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "text"
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                } else if (currentOptions.some(opt => opt.trim())) {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter(opt => opt.trim())
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                }
            }

            const updatePayload = {
                title: formState.jobTitle,
                location,
                employment_type: formState.employmentType,
                work_preference: formState.workPreference,
                specialty: formState.specialty,
                requires_license: formState.locationOrLicenceRestrictions === "yes",
                sponsorship_available: formState.visaSponsorshipOffered,
                job_description: formState.about || formState.roleDescription,
                role_description: formState.roleDescription,
                preferred_candidate: formState.preferredCandidate,
                duties_and_responsibilities: formState.dutiesAndResponsibilities,
                requirements_specialty: formState.requirementsSpecialty || formState.specialty,
                experience_level: formState.experienceLevel || null,
                years_of_experience: formState.yearsOfExperience || null,
                budget_for_role: formState.budgetForRole || null,
                salary_currency: formState.salaryCurrency || "ZAR",
                salary_period: formState.salaryPeriod || "yearly",
                salary_range: formState.budgetForRole || null, // Also update salary_range for display compatibility
                required_skills: Array.isArray(formState.requiredSkills) ? formState.requiredSkills : [],
                other_preferences: formState.otherPreferences,
                country: formState.country,
                country_code: formState.countryCode,
                province: formState.province,
                hiring_restrictions: formState.locationOrLicenceRestrictionsText || "",
                screening_questions: formState.screeningQuestions,
                screening_questions_list: finalScreeningQuestionsList.length > 0 ? finalScreeningQuestionsList : null,
                drivers_license_required: formState.driversLicenseRequired,
                drivers_license_answer: driversLicenseAnswer,
                visa_sponsorship_offered: formState.visaSponsorshipOffered,
                visa_sponsorship_answer: visaSponsorshipAnswer
            };

            const { error } = await supabase
                .from("internal_job_listings")
                .update(updatePayload)
                .eq("id", initialJob.id)
                .eq("employer_id", employerUser.id);

            if (error) throw error;
            onSuccess?.();
            onClose?.();
        } catch (err) {
            console.error("Failed to update job listing", err);
            alert("Failed to update job listing. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 employer-jobs-modal-overlay">
            <style>{`
                @media (max-width: 1024px) {
                    .employer-jobs-modal {
                        width: 100% !important;
                        border-radius: 1.25rem !important;
                        margin-top: 0 !important;
                    }
                    .employer-jobs-modal-header,
                    .employer-jobs-modal-footer {
                        padding: 1rem !important;
                    }
                    .employer-jobs-modal-body {
                        padding: 1rem !important;
                        max-height: 60vh !important;
                    }
                    .employer-jobs-modal-body .grid {
                        gap: 0.75rem !important;
                    }
                    .employer-jobs-modal-footer {
                        flex-direction: column !important;
                        gap: 0.75rem !important;
                    }
                    .employer-jobs-modal-footer button {
                        width: 100% !important;
                    }
                }
            `}</style>
            <div
                className="absolute inset-0 bg-black/30"
                onClick={() => {
                    if (!saving) onClose?.();
                }}
            />
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden employer-jobs-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b employer-jobs-modal-header">
                    {/* modal heading */}
                    <h2 className="text-lg font-semibold">
                        {mode === "edit" ? "Edit job post" : "Create new job post"}
                    </h2>

                    {/* set job as dev mode checkbox */}
                    {user?.email.includes('@fourminutemedicine.com') && (<div className="flex items-center gap-2">
                        <input type="checkbox" id="devMode" className="h-4 w-4 text-blue-600 border-gray-300 rounded" 
                        checked={formState.is_dev_job} 
                        onChange={(e) => setFormState(prev => ({ ...prev, is_dev_job: e.target.checked }))} 
                        />
                        <label htmlFor="devMode" className="text-sm font-medium text-gray-700">Set job as dev mode (for testing purposes)</label>
                    </div>)}

                    {/* close button */}
                    <button
                        onClick={() => {
                            if (!saving) onClose?.();
                        }}
                        className="p-2 rounded-full hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto employer-jobs-modal-body">
                    {jobFormStep === 2 && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Job Title*</label>
                                    <input
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.jobTitle}
                                        onChange={(e) => setFormState(prev => ({ ...prev, jobTitle: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Role category*</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.specialty}
                                        onChange={(e) => setFormState(prev => ({ ...prev, specialty: e.target.value }))}
                                    >
                                        <option value="">Select a role</option>
                                        {Array.isArray(roleCategories) && roleCategories.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Describe the role?</label>
                                <textarea
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                    placeholder="Provide a brief overview of the position"
                                    value={formState.roleDescription}
                                    onChange={(e) => setFormState(prev => ({ ...prev, roleDescription: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role Requirements?</label>
                                <textarea
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                    placeholder="List the qualifications, skills and experience required for this position."
                                    value={formState.preferredCandidate}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (value.length === 1 && !value.startsWith('●')) {
                                            value = '●  ' + value;
                                        }
                                        setFormState(prev => ({ ...prev, preferredCandidate: value }));
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const textarea = e.target;
                                            const start = textarea.selectionStart;
                                            const end = textarea.selectionEnd;
                                            const value = formState.preferredCandidate || '';
                                            const newValue = value.substring(0, start) + '\n●  ' + value.substring(end);
                                            setFormState(prev => ({ ...prev, preferredCandidate: newValue }));
                                            setTimeout(() => {
                                                textarea.selectionStart = textarea.selectionEnd = start + 4;
                                            }, 0);
                                        }
                                    }}
                                    onFocus={(e) => {
                                        if (!formState.preferredCandidate) {
                                            setFormState(prev => ({ ...prev, preferredCandidate: '●  ' }));
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Duties and responsibilities?</label>
                                <textarea
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                    placeholder="List day-to-day responsibilities and expectations for this role."
                                    value={formState.dutiesAndResponsibilities}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (value.length === 1 && !value.startsWith('●')) {
                                            value = '●  ' + value;
                                        }
                                        setFormState(prev => ({ ...prev, dutiesAndResponsibilities: value }));
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const textarea = e.target;
                                            const start = textarea.selectionStart;
                                            const end = textarea.selectionEnd;
                                            const value = formState.dutiesAndResponsibilities || '';
                                            const newValue = value.substring(0, start) + '\n●  ' + value.substring(end);
                                            setFormState(prev => ({ ...prev, dutiesAndResponsibilities: newValue }));
                                            setTimeout(() => {
                                                textarea.selectionStart = textarea.selectionEnd = start + 4;
                                            }, 0);
                                        }
                                    }}
                                    onFocus={(e) => {
                                        if (!formState.dutiesAndResponsibilities) {
                                            setFormState(prev => ({ ...prev, dutiesAndResponsibilities: '●  ' }));
                                        }
                                    }}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Employment Type*</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.employmentType}
                                        onChange={(e) => setFormState(prev => ({ ...prev, employmentType: e.target.value }))}
                                    >
                                        <option value="">Select employment type</option>
                                        <option value="full-time">Full-time</option>
                                        <option value="part-time">Part-time</option>
                                        <option value="contract">Contract</option>
                                        <option value="temporary">Temporary</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Work preference*</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.workPreference}
                                        onChange={(e) => setFormState(prev => ({ ...prev, workPreference: e.target.value }))}
                                    >
                                        <option value="">Select work preference</option>
                                        <option value="remote">Remote</option>
                                        <option value="on-site">On-site</option>
                                        <option value="hybrid">Hybrid</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Country*</label>
                                    <Select
                                        options={[...countries].sort((a, b) => (
                                            a.label === "South Africa" ? -1 : b.label === "South Africa" ? 1 : a.label.localeCompare(b.label)
                                        ))}
                                        value={countries.find(c => c.value === formState.countryCode || c.label === formState.country) || null}
                                        onChange={(selectedOption) => {
                                            const countryCode = selectedOption?.value || "";
                                            setFormState(prev => ({
                                                ...prev,
                                                country: selectedOption?.label || "",
                                                countryCode,
                                                province: ""
                                            }));
                                        }}
                                        placeholder="Select country..."
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: "9999px",
                                                borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
                                                boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
                                                "&:hover": { borderColor: "#9ca3af" }
                                            })
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Province/State*</label>
                                    {formState.countryCode ? (
                                        <Select
                                            options={State.getStatesOfCountry(formState.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))}
                                            value={formState.province ? { value: formState.province, label: formState.province } : null}
                                            onChange={(selectedOption) => {
                                                setFormState(prev => ({ ...prev, province: selectedOption?.label || "" }));
                                            }}
                                            placeholder="Select province/state..."
                                            isSearchable
                                            classNamePrefix="react-select"
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: "9999px",
                                                    borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
                                                    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
                                                    "&:hover": { borderColor: "#9ca3af" }
                                                })
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            className="rounded-full border border-gray-300 px-4 py-2 w-full bg-gray-50"
                                            value={formState.province}
                                            onChange={(e) => setFormState(prev => ({ ...prev, province: e.target.value }))}
                                            placeholder="Select country first"
                                            disabled
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Language requirement*</label>
                                    <Select
                                        isMulti
                                        options={languages.map(lang => ({ value: lang, label: lang }))}
                                        value={Array.isArray(formState.languageRequirement)
                                            ? formState.languageRequirement.map(lang => ({ value: lang, label: lang }))
                                            : []
                                        }
                                        onChange={(selectedOptions) => {
                                            setFormState(prev => ({
                                                ...prev,
                                                languageRequirement: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                            }));
                                        }}
                                        placeholder="Select language(s)..."
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: "9999px",
                                                borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
                                                minHeight: "42px",
                                                "&:hover": { borderColor: "#9ca3af" }
                                            })
                                        }}
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <label className="block text-sm font-medium text-gray-700">Location or licence restrictions*</label>
                                    </div>
                                    <div className="flex gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const answer = formState.locationOrLicenceRestrictions === "yes" ? null : "yes";
                                                setFormState(prev => ({
                                                    ...prev,
                                                    locationOrLicenceRestrictions: answer,
                                                    locationOrLicenceRestrictionsText: answer === null ? "" : prev.locationOrLicenceRestrictionsText
                                                }));
                                            }}
                                            className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                formState.locationOrLicenceRestrictions === "yes"
                                                    ? "bg-gray-800 text-white border-gray-800"
                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                            }`}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const answer = formState.locationOrLicenceRestrictions === "no" ? null : "no";
                                                setFormState(prev => ({
                                                    ...prev,
                                                    locationOrLicenceRestrictions: answer,
                                                    locationOrLicenceRestrictionsText: answer === null ? "" : prev.locationOrLicenceRestrictionsText
                                                }));
                                            }}
                                            className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                formState.locationOrLicenceRestrictions === "no"
                                                    ? "bg-gray-800 text-white border-gray-800"
                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                            }`}
                                        >
                                            No
                                        </button>
                                    </div>
                                    {formState.locationOrLicenceRestrictions === "yes" && (
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">What are the restrictions</label>
                                            <input
                                                type="text"
                                                className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                value={formState.locationOrLicenceRestrictionsText}
                                                onChange={(e) => setFormState(prev => ({ ...prev, locationOrLicenceRestrictionsText: e.target.value }))}
                                                placeholder="Enter restrictions"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {jobFormStep === 3 && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Specialty*</label>
                                    {isCustomSpecialty ? (
                                        <div className="space-y-2">
                                            <input
                                                className="rounded-full border border-gray-300 px-4 py-2 w-full text-sm"
                                                placeholder="Enter your specialty"
                                                value={formState.requirementsSpecialty}
                                                onChange={(e) =>
                                                    setFormState(prev => ({ ...prev, requirementsSpecialty: e.target.value }))
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="text-xs text-blue-600 hover:underline"
                                                onClick={resetSpecialtyToSelect}
                                            >
                                                choose from list
                                            </button>
                                        </div>
                                    ) : (
                                        <select
                                            className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                            value={formState.requirementsSpecialty}
                                            onChange={(e) => handleSpecialtySelect(e.target.value)}
                                        >
                                            <option value="">Select a specialty</option>
                                            {Array.isArray(specialties) && specialties.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                            <option value="Not applicable">Not applicable</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Experience level eg. Grade</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.experienceLevel}
                                        onChange={(e) => setFormState(prev => ({ ...prev, experienceLevel: e.target.value }))}
                                    >
                                        <option value="">Select experience level</option>
                                        <option value="medical-student">Medical Student</option>
                                        <option value="junior-doctor">Junior Doctor (Intern)</option>
                                        <option value="medical-officer">Medical Officer</option>
                                        <option value="registrar">Registrar</option>
                                        <option value="fellow">Fellow</option>
                                        <option value="specialist">Specialist</option>
                                        <option value="consultant">Consultant</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Budget for role</label>
                                    <div className="flex gap-1">
                                        <select
                                            className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[75px]"
                                            value={formState.salaryCurrency || "ZAR"}
                                            onChange={(e) => setFormState(prev => ({ ...prev, salaryCurrency: e.target.value }))}
                                        >
                                            {currencies.map((currency) => (
                                                <option key={currency.code} value={currency.code}>{currency.code}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="rounded-full border border-gray-300 px-3 py-2 flex-1 min-w-0"
                                            value={formState.budgetForRole}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/[^0-9\s,\-–+]/g, '');
                                                // Only allow "+" after a digit, remove any "+" that doesn't follow a digit
                                                value = value.replace(/([^0-9])\+/g, '$1').replace(/^\+/g, '');
                                                setFormState(prev => ({ ...prev, budgetForRole: value }));
                                            }}
                                            placeholder="Range"
                                        />
                                        <select
                                            className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[85px]"
                                            value={formState.salaryPeriod || "yearly"}
                                            onChange={(e) => setFormState(prev => ({ ...prev, salaryPeriod: e.target.value }))}
                                        >
                                            {salaryPeriods.map((period) => (
                                                <option key={period.value} value={period.value}>{period.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Years of Experience</label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                        value={formState.yearsOfExperience}
                                        onChange={(e) => setFormState(prev => ({ ...prev, yearsOfExperience: e.target.value }))}
                                        placeholder="e.g. 3"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">What skills are required?</label>
                                <div className="relative">
                                    <input
                                        className="rounded-full border border-gray-300 px-4 py-2 w-full pr-10"
                                        value={skillInput}
                                        onChange={(e) => setSkillInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addSkill(skillInput);
                                            }
                                        }}
                                        placeholder="Enter skill and press Enter"
                                    />
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400">
                                            <circle cx="11" cy="11" r="8" />
                                            <path d="m21 21-4.35-4.35" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">'Enter' to add Skills</p>
                                {formState.requiredSkills.length > 0 && (
                                    <div className="mt-3">
                                        <div className="flex flex-wrap gap-2">
                                            {formState.requiredSkills.map((skill, index) => (
                                                <span key={index} className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                                                    {skill}
                                                    <button onClick={() => removeSkill(index)} className="text-gray-500 hover:text-red-600">×</button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700 mb-3">Other preferences</label>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormState(prev => ({
                                            ...prev,
                                            screeningQuestions: !prev.screeningQuestions
                                        }))}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                            formState.screeningQuestions
                                                ? "bg-black text-white border-black"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Screening questions
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newValue = !formState.driversLicenseRequired;
                                            setFormState(prev => ({
                                                ...prev,
                                                driversLicenseRequired: newValue
                                            }));
                                            if (!newValue) setDriversLicenseAnswer(null);
                                        }}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                            formState.driversLicenseRequired
                                                ? "bg-black text-white border-black"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Drivers license required
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newValue = !formState.visaSponsorshipOffered;
                                            setFormState(prev => ({
                                                ...prev,
                                                visaSponsorshipOffered: newValue
                                            }));
                                            if (!newValue) setVisaSponsorshipAnswer(null);
                                        }}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                            formState.visaSponsorshipOffered
                                                ? "bg-black text-white border-black"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Visa sponsorship offered
                                    </button>
                                </div>
                                {screeningQuestionsList.length > 0 && (
                                    <div className="mb-4 space-y-3">
                                        <div className="text-sm font-medium text-gray-700 mb-2">Saved Questions:</div>
                                        {screeningQuestionsList.map((savedQuestion, qIndex) => (
                                            <div key={qIndex} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                                <div className="flex items-start justify-between mb-2">
                                                    <p className="text-sm font-medium text-gray-900">{savedQuestion.question}</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeScreeningQuestion(qIndex)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                {savedQuestion.type === "multiple-choice" && savedQuestion.options && Array.isArray(savedQuestion.options) && (
                                                    <div className="space-y-1 mt-2">
                                                        {savedQuestion.options.map((option, optIndex) => (
                                                            <div key={optIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                                                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                    {String.fromCharCode(9311 + optIndex)}
                                                                </div>
                                                                <span>{option}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {formState.screeningQuestions && (
                                    <div className="mb-4 space-y-3">
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Type your question"
                                                value={currentQuestion}
                                                onChange={(e) => setCurrentQuestion(e.target.value)}
                                                className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm"
                                            />
                                        </div>
                                        
                                        {/* Question Type Buttons - matching image style */}
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setCurrentQuestionType("multiple-choice")}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                    currentQuestionType === "multiple-choice"
                                                        ? 'bg-gray-900 text-white border-gray-900'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Multi-choice question
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentQuestionType("text")}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                    currentQuestionType === "text"
                                                        ? 'bg-gray-900 text-white border-gray-900'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Long answer
                                            </button>
                                        </div>
                                        
                                        {/* Options input - only show for multi-choice */}
                                        {currentQuestionType === "multiple-choice" && (
                                            <>
                                                <div className="space-y-2">
                                                    {currentOptions.map((option, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                {String.fromCharCode(9311 + index)}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                placeholder={`Option ${index + 1}`}
                                                                value={option}
                                                                onChange={(e) => updateScreeningOption(index, e.target.value)}
                                                                className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm bg-gray-50"
                                                            />
                                                            {currentOptions.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeScreeningOption(index)}
                                                                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={addScreeningOption}
                                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                                >
                                                    <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </div>
                                                    Add option
                                                </button>
                                            </>
                                        )}
                                        
                                        {/* Preview textarea for long answer */}
                                        {currentQuestionType === "text" && (
                                            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                                <p className="text-xs text-gray-500 mb-2">Preview</p>
                                                <textarea
                                                    disabled
                                                    placeholder="Type your answer here..."
                                                    rows={4}
                                                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white resize-y cursor-not-allowed opacity-75"
                                                />
                                            </div>
                                        )}
                                        
                                        <button
                                            type="button"
                                            onClick={addScreeningQuestion}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Add question
                                        </button>
                                    </div>
                                )}
                                {formState.driversLicenseRequired && (
                                    <div className="mb-4 space-y-3">
                                        <div className="text-sm font-medium text-gray-900 mb-3">Do you have a driver's licence?</div>
                                        <div className="space-y-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const answer = driversLicenseAnswer === "yes" ? null : "yes";
                                                    setDriversLicenseAnswer(answer);
                                                    setFormState(prev => ({ ...prev, driversLicenseAnswer: answer }));
                                                }}
                                                className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                    driversLicenseAnswer === "yes" ? "bg-black text-white border-black" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                }`}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const answer = driversLicenseAnswer === "no" ? null : "no";
                                                    setDriversLicenseAnswer(answer);
                                                    setFormState(prev => ({ ...prev, driversLicenseAnswer: answer }));
                                                }}
                                                className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                    driversLicenseAnswer === "no" ? "bg-black text-white border-black" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                }`}
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {formState.visaSponsorshipOffered && (
                                    <div className="mb-4 space-y-3">
                                        <div className="text-sm font-medium text-gray-900 mb-3">Do you need a visa sponsorship?</div>
                                        <div className="space-y-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const answer = visaSponsorshipAnswer === "yes" ? null : "yes";
                                                    setVisaSponsorshipAnswer(answer);
                                                    setFormState(prev => ({ ...prev, visaSponsorshipAnswer: answer }));
                                                }}
                                                className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                    visaSponsorshipAnswer === "yes" ? "bg-black text-white border-black" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                }`}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const answer = visaSponsorshipAnswer === "no" ? null : "no";
                                                    setVisaSponsorshipAnswer(answer);
                                                    setFormState(prev => ({ ...prev, visaSponsorshipAnswer: answer }));
                                                }}
                                                className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                    visaSponsorshipAnswer === "no" ? "bg-black text-white border-black" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                }`}
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <textarea
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                    placeholder="Any other preferences or requirements..."
                                    value={formState.otherPreferences}
                                    onChange={(e) => setFormState(prev => ({ ...prev, otherPreferences: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t flex items-center gap-3 employer-jobs-modal-footer">
                    <button
                        onClick={() => {
                            if (!saving) onClose?.();
                        }}
                        className="px-5 py-2 rounded-full border text-sm"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    {jobFormStep === 2 ? (
                        <button
                            onClick={handleNextStep}
                            className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm"
                        >
                            Next step
                        </button>
                    ) : (
                        <>
                            <button onClick={handleBackStep} className="px-5 py-2 rounded-full border text-sm">
                                Back
                            </button>
                            <button
                                onClick={handleUpdateJob}
                                disabled={saving}
                                className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm disabled:opacity-60"
                            >
                                {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create job"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobPostFormModal;


